export const HOME = '/';

export const AUTH = '/auth';
export const LOGIN = AUTH + '/login';
export const PORTAL_LOGIN = AUTH + '/portal-login';

export const COMPANY_LIST = '/company-list';
export const DASHBOARD = '/dashboard';

// In-app notifications (served by portal-service, public.notifications scoped to ERPBOI_STORE).
// Reached from the topbar bell's "View all"; not a sidebar menu item.
export const NOTIFICATIONS = '/notifications';

export const SETUP = '/setup';
export const PRODUCT_LIST = SETUP + '/product/list';
export const UOM_LIST = SETUP + '/uom';
export const CATEGORY_LIST = SETUP + '/category';
export const SUB_CATEGORY_LIST = SETUP + '/sub-category';
export const MASTER_DATA_LIST = SETUP + '/master-data';
export const VENDOR_LIST = SETUP + '/vendor';
export const APPROVAL_WORKFLOW = SETUP + '/approval-workflow';
export const ITEM_CODE_SETTINGS = SETUP + '/item-code-settings';

export const APPROVALS = '/approvals';
export const MY_REQUESTS = APPROVALS + '/my-requests';
export const PENDING_APPROVALS = APPROVALS + '/pending-approvals';
// The dedicated decision page. Gated on PENDING_APPROVALS (not a code of its own), so whoever can
// see the queue can open a row from it -- a separate feature code would need an auth.features row in
// BOTH databases, and a missing one means "always allow", which is the wrong default for a page that
// approves things.
export const PENDING_APPROVAL_DETAIL = PENDING_APPROVALS + '/:requestId';
export const WAREHOUSE_LIST = SETUP + '/warehouse';
export const CUSTOMER = SETUP + '/customer';
export const SUPPLIER = SETUP + '/supplier';

export const RESTORA = '/restora';
export const DISH_CATEGORY = RESTORA + '/dish-category';
export const DISHES = RESTORA + '/dishes';
export const DISH_ORDER = RESTORA + '/dish-order';

//Procurement
// Procurement is now the BUYING menu only -- the two ways money is committed to a supplier.
// The store-side chain documents that used to live here (SPR, challan, QC, MRR) moved under
// Inventory on 2026-09-23, because they are raised and worked by the store, not the buyer.
export const PROCUREMENT = '/procurement';
export const PURCHASE_ORDER = PROCUREMENT + '/purchase-order';
export const PURCHASE_ORDER_VIEW: ({ id }) => string = ({ id }) =>
  `${PROCUREMENT}/purchase-order/view/${id}`;
// Buying WITHOUT an SPR: a different document on a different table (store.purchases via
// POST /purchase/direct-add), named in the refusal a sourceless purchase order gets.
export const DIRECT_PURCHASE = PROCUREMENT + '/direct-purchase';

//inventory
export const INVENTORY = '/inventory';
export const INTERNAL_REQUISITION = INVENTORY + '/internal-requisition';
export const INTERNAL_REQUISITION_ADD = INVENTORY + '/internal-requisition/add';
export const INTERNAL_REQUISITION_EDIT: ({ id }) => string = ({ id }) =>
  `${INVENTORY}/internal-requisition/edit/${id}`;
// Read-only detail, as a routed PAGE rather than a drawer. A document worth opening is worth a URL:
// it can be linked to, refreshed, opened in a second tab and arrived at from a notification, none of
// which a drawer keyed on list state can do. Every *_VIEW route below is registered with the SAME
// routeCode as the list it is opened from -- PrivateRoutes reads a MISSING code as "always allow",
// so leaving one off would make the detail the one ungated screen in its group.
export const INTERNAL_REQUISITION_VIEW: ({ id }) => string = ({ id }) =>
  `${INVENTORY}/internal-requisition/view/${id}`;
export const STOCK_TRANSFER = INVENTORY + '/transfer-stock';
export const STOCK_TRANSFER_ADD = INVENTORY + '/transfer-stock/add';
export const STOCK_TRANSFER_EDIT: ({id}) => string = ({id}) =>
  `${INVENTORY}/transfer-stock/edit/${id}`;
// The only way to READ a decided issue: the form refuses to open one, and ISSUE auto-approves
// (no approval_doc_config row), so most of the register -- and all of the migrated history -- was
// unreadable beyond the rolled-up quantity on the list row.
export const STOCK_TRANSFER_VIEW: ({id}) => string = ({id}) =>
  `${INVENTORY}/transfer-stock/view/${id}`;
export const REQUISITION_ISSUE = INVENTORY + '/fulfil-requisition';
// The same issue view as STOCK_TRANSFER_VIEW, on a route of its own so it is gated on Fulfil
// Requisition's code: a store user granted only this screen must not need Transfer to read a row.
export const REQUISITION_ISSUE_VIEW: ({id}) => string = ({id}) =>
  `${INVENTORY}/fulfil-requisition/view/${id}`;
// The procurement chain, in the order the documents are raised:
// requisition -> SPR -> PO -> challan -> QC -> MRR. Only the PO is a Procurement screen; the
// other four are the store's own work and sit here, between the requisition they answer and
// the stock they land in. Moving the route is safe in THIS app specifically: stock gates on
// feature.code === routeCode (ROUTE_CODES), not on auth.features.url, and those rows carry an
// empty url -- so the codes are untouched and every existing grant still applies.
export const SPR = INVENTORY + '/spr';
export const SPR_VIEW: ({ id }) => string = ({ id }) => `${INVENTORY}/spr/view/${id}`;
export const CHALLAN = INVENTORY + '/challan';
export const CHALLAN_VIEW: ({ id }) => string = ({ id }) => `${INVENTORY}/challan/view/${id}`;
export const QUALITY_CERTIFICATE = INVENTORY + '/quality-certificate';
export const QUALITY_CERTIFICATE_VIEW: ({ id }) => string = ({ id }) =>
  `${INVENTORY}/quality-certificate/view/${id}`;
export const MRR = INVENTORY + '/mrr';
export const MRR_VIEW: ({ id }) => string = ({ id }) => `${INVENTORY}/mrr/view/${id}`;
export const STORE_ADJUSTMENT = INVENTORY + '/store-adjustment';
export const STOCK = INVENTORY + '/stock';
// Stock adjustment: a real document since S19 (store.adjustment), replacing the ad-hoc
// post-straight-to-the-ledger screen that used to live on this route. The route string is
// UNCHANGED on purpose -- gating is exact equality between this constant and the ACL feature
// code/url, so moving it would need a matching DB change in both databases to stay reachable.
export const STOCK_ADJUSTMENT = INVENTORY + '/stock-adjustment';
export const STOCK_ADJUSTMENT_ADD = INVENTORY + '/stock-adjustment/add';
export const STOCK_ADJUSTMENT_EDIT: ({id}) => string = ({id}) =>
  `${INVENTORY}/stock-adjustment/edit/${id}`;
// Damage: the store write-off, ported from the legacy str_damage slip.
export const DAMAGE = INVENTORY + '/damage';
export const DAMAGE_ADD = INVENTORY + '/damage/add';
export const DAMAGE_EDIT: ({id}) => string = ({id}) => `${INVENTORY}/damage/edit/${id}`;
// Gate pass: the OUTBOUND one (authority for goods to leave). Not the inbound Secu_GPass, which
// in this system is the challan.
export const GATE_PASS = INVENTORY + '/gate-pass';
export const GATE_PASS_ADD = INVENTORY + '/gate-pass/add';
export const GATE_PASS_EDIT: ({id}) => string = ({id}) => `${INVENTORY}/gate-pass/edit/${id}`;

//sales
export const SALES = '/sales';
export const WEB_SALES_ORDER = SALES + '/web-sale-order';
export const POS_SALES_ORDER = SALES + '/pos-sale-order';
export const SALES_DETAILS = ({ id }) => `${WEB_SALES_ORDER}/${id}`;
export const SALES_RETURN = ({ id }) => `${WEB_SALES_ORDER}/${id}/return`;
export const SALES_REFUND = ({ id }) => `${WEB_SALES_ORDER}/${id}/refund`;

//settings
export const SETTINGS = '/settings';
export const BILLING_INVOICE = SETTINGS + '/billing-invoice';
export const COUPON = SETTINGS + '/coupon';
export const COUPON_ADD = SETTINGS + '/add';
export const DELIVERY_SETUP = SETTINGS + '/delivery-setup';
export const STAFF_USER = SETTINGS + '/staff-users';


//
export const POS = '/pos';
export const POS_SHIFT = '/pos-shift';

//Reports
export const REPORTS = '/reports';
export const REPORT_INVENTORY = REPORTS + '/inventory';
export const REPORT_PURCHASES = REPORTS + '/purchases';
// Moved out of Inventory: it is a read-only movement history, not a place anything is done.
export const REPORT_STOCK_LEDGER = REPORTS + '/stock-ledger';
// The stock card: one item, one warehouse, opening -> movements -> closing.
export const REPORT_PRODUCT_LEDGER = REPORTS + '/product-ledger';
// One item's stock card. The id is the PRODUCT; the variant, the warehouse and the date window ride
// as query params, because a card is only meaningful inside the report that framed it -- an opening
// balance summed across stores describes nothing while looking entirely plausible. Putting them in
// the URL is also what makes the card linkable at all: it carries its own scope instead of
// borrowing the list's component state.
export const REPORT_PRODUCT_LEDGER_VIEW: ({ id }) => string = ({ id }) =>
  `${REPORTS}/product-ledger/view/${id}`;
// Goods Received Note: the supplier-facing view across challan -> QC -> MRR, per delivered line.
export const REPORT_GRN = REPORTS + '/grn';
export const REPORT_GRN_VIEW: ({ id }) => string = ({ id }) => `${REPORTS}/grn/view/${id}`;
// The internal counterpart of GRN: one requisition line walked down the whole procurement chain
// (requisition -> SPR -> PO -> challan -> QC -> MRR), one row per path it took.
export const REPORT_REQ_TRACKING = REPORTS + '/requisition-tracking';
export const REPORT_REQ_TRACKING_VIEW: ({ id }) => string = ({ id }) =>
  `${REPORTS}/requisition-tracking/view/${id}`;
// The legacy store system's "Detail Information of SPR": one SPR's lines with their requisitions,
// stock position, consumption and last purchase, and the approval signatures.
export const REPORT_SPR_DETAIL = REPORTS + '/spr-detail';
export const REPORT_SPR_DETAIL_VIEW: ({ id }) => string = ({ id }) =>
  `${REPORTS}/spr-detail/view/${id}`;
export const RPT_DAILY_SUMMARY = '/rpt-daily-summary';
export const RPT_PURCHASE_ORDER = '/rpt-purchase-order';
export const RPT_PURCHASES = '/rpt-purchases';
export const RPT_SALES = '/rpt-sales';
export const RPT_RETURN = '/rpt-return';
export const RPT_REFUND = '/rpt-refund';
export const RPT_EXPENSES = '/rpt-expenses';
export const RPT_STOCK = '/rpt-stock';
